import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

/**
 * RelatedContent — luxury editorial widget showing related posts and matching region.
 *
 * Props:
 *  - type: "blog" | "regiao"
 *  - slug: string (post slug/id or region slug)
 */
const RelatedContent = ({ type, slug }) => {
  const [data, setData] = useState({ posts: [], region: null });
  const [loading, setLoading] = useState(true);
  const { language, localizedPath } = useLanguage();

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/related/${type}/${slug}`)
      .then((res) => {
        if (!cancelled) setData(res.data || { posts: [], region: null });
      })
      .catch(() => {
        if (!cancelled) setData({ posts: [], region: null });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, slug]);

  if (loading || (!data.posts?.length && !data.region)) return null;

  const { posts, region } = data;

  return (
    <aside data-testid="related-content" className="w-full">
      <p
        className="inline-flex items-center gap-3 text-editorial-bronze mb-10"
        style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '0.62rem',
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        <span className="block w-7 h-px bg-editorial-bronze" />
        {language === 'pt' ? 'Continuar a leitura' : 'Continue reading'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-12">
        {region && (
          <div className="md:col-span-4">
            <p
              className="text-editorial-muted/60 mb-4"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.58rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              {language === 'pt' ? 'Região' : 'Region'}
            </p>
            <Link
              to={localizedPath('/regions')}
              className="group block"
              data-testid={`related-region-${region.slug}`}
            >
              {region.image && (
                <div className="aspect-[4/3] overflow-hidden bg-editorial-sand mb-5">
                  <img
                    src={resolveImg(region.image)}
                    alt={region.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              )}
              <h3
                className="text-editorial-charcoal font-light group-hover:text-editorial-bronze transition-colors mb-2"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '1.5rem',
                  lineHeight: '1.2',
                }}
              >
                {region.name}
              </h3>
              {region.description && (
                <p
                  className="text-editorial-muted font-light line-clamp-3"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.85rem',
                    lineHeight: '1.85',
                  }}
                >
                  {region.description}
                </p>
              )}
            </Link>
          </div>
        )}

        {posts.length > 0 && (
          <div className={region ? 'md:col-span-8' : 'md:col-span-12'}>
            <p
              className="text-editorial-muted/60 mb-4"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.58rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              {language === 'pt' ? 'Ensaios relacionados' : 'Related essays'}
            </p>
            <ul className="space-y-7 divide-y divide-editorial-sand-deep/30">
              {posts.map((p, idx) => (
                <li key={p.slug} className={idx === 0 ? '' : 'pt-7'}>
                  <Link
                    to={localizedPath(`/journal/${p.slug}`)}
                    className="group flex items-start gap-6"
                    data-testid={`related-post-${p.slug}`}
                  >
                    {p.image && (
                      <div className="w-24 h-24 md:w-28 md:h-28 overflow-hidden bg-editorial-sand shrink-0">
                        <img
                          src={resolveImg(p.image)}
                          alt={p.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      {p.category && (
                        <p
                          className="text-editorial-bronze mb-2"
                          style={{
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.55rem',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                          }}
                        >
                          {p.category}
                        </p>
                      )}
                      <h4
                        className="text-editorial-charcoal font-light group-hover:text-editorial-bronze transition-colors"
                        style={{
                          fontFamily: 'Cormorant Garamond, serif',
                          fontSize: '1.25rem',
                          lineHeight: '1.28',
                        }}
                      >
                        {p.title}
                      </h4>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
};

export default RelatedContent;
